/*
  viral-language-banner.js  (sections/viral-language-banner.liquid)

  Suggests, and never forces, another storefront language:
  1. reads the published languages from the JSON the section prints (localization.available_languages),
  2. walks navigator.languages in the visitor's order of preference and stops at the first one the store has,
  3. if that language is not the current one and the banner was not dismissed, un-hides the banner,
  4. the button submits Dawn's localization form (locale_code + return_to), so Shopify reloads the same page
     in that language. There is no automatic redirect anywhere in this file.

  I18N: no user-facing text lives in this file.
  The sentence has to be written in the SUGGESTED language, because the visitor may not read the current one,
  and Liquid only translates into the locale of the request. So the element asks Shopify for this same section
  rendered in the suggested locale (Section Rendering API on that locale's root URL:
  `<root_url>?section_id=<section id>`) and copies the strings from the data-self-* / data-dismiss / data-label
  attributes of that response. Every word therefore comes from the theme locale files (viral.language_banner.*)
  of the suggested language, including merchant edits made in the theme language editor or Translate & Adapt.
  If that request fails, the banner falls back to the current-language strings the section printed, with the
  language's own name filled in, e.g. "¿Ver la tienda en English?". One same-origin GET, only when there is
  something to suggest and the banner was not dismissed.

  Storage: one localStorage flag, written only after the visitor acts (dismiss, switch, or picking a language
  in Dawn's own selector). It is a functional preference, holds no personal data, and every access is wrapped
  in try/catch because storage can be blocked.
*/

if (!customElements.get('viral-language-banner')) {
  // declared inside the guard (not at top level) so a second copy of this script tag cannot throw
  const VIRAL_LANGUAGE_BANNER_KEY = 'viral-language-banner:dismissed';

  const primarySubtag = (tag) => String(tag).toLowerCase().split('-')[0];

  const rememberChoice = () => {
    try {
      window.localStorage.setItem(VIRAL_LANGUAGE_BANNER_KEY, '1');
    } catch (error) {
      // storage blocked: the banner may show again on the next page, nothing else breaks
    }
  };

  // A visitor who picks a language in Dawn's own selector has made up their mind: stop suggesting.
  document.addEventListener('click', (event) => {
    const item = event.target.closest && event.target.closest('localization-form a[data-value]');
    if (item && item.closest('form').querySelector('input[name="locale_code"]')) rememberChoice();
  });

  customElements.define(
    'viral-language-banner',
    class ViralLanguageBanner extends HTMLElement {
      connectedCallback() {
        this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
        this.form = this.querySelector('form');
        this.closeButton = this.querySelector('[data-dismiss-target]');
        if (!this.form || !this.closeButton) return;

        const languages = this.readLanguages();
        const current = this.dataset.current || document.documentElement.lang;
        let suggestion = this.findSuggestion(languages, current);

        // Theme editor: always show a preview so the merchant can see and style the banner.
        if (this.designMode && !suggestion) {
          suggestion = languages.find((language) => language.iso_code.toLowerCase() !== current.toLowerCase());
        }

        if (!suggestion) return;
        if (!this.designMode && this.wasDismissed()) return;

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.closeButton.addEventListener('click', this.onDismiss.bind(this));

        // Stays hidden until the copy is in place, so the visitor never sees it change language.
        this.fetchCopy(suggestion).then((copy) => {
          if (!this.isConnected) return;
          this.fill(suggestion, copy);
          this.hidden = false;
        });
      }

      /* The section rendered in the suggested locale. Resolves to null when anything goes wrong. */
      fetchCopy(language) {
        const sectionId = this.dataset.sectionId;
        if (!sectionId || !language.root_url || typeof fetch !== 'function') return Promise.resolve(null);

        const url = `${language.root_url}${language.root_url.indexOf('?') === -1 ? '?' : '&'}section_id=${encodeURIComponent(sectionId)}`;
        return fetch(url)
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
          .then((text) => {
            const source = new DOMParser().parseFromString(text, 'text/html').querySelector('viral-language-banner');
            if (!source || !source.dataset.selfMessage || !source.dataset.selfAction) return null;
            // the response must really be in the suggested language, otherwise its sentence names the wrong one
            if ((source.dataset.current || '').toLowerCase() !== language.iso_code.toLowerCase()) return null;
            return {
              message: source.dataset.selfMessage,
              action: source.dataset.selfAction,
              dismiss: source.dataset.dismiss,
              label: source.dataset.label,
            };
          })
          .catch(() => null);
      }

      readLanguages() {
        try {
          const data = JSON.parse(this.querySelector('script[data-languages]').textContent);
          return Array.isArray(data) ? data.filter((language) => language && language.iso_code) : [];
        } catch (error) {
          return [];
        }
      }

      findSuggestion(languages, current) {
        const preferred =
          navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
        const currentTag = current.toLowerCase();

        for (const tag of preferred) {
          if (!tag) continue;
          const wanted = tag.toLowerCase();
          const sameFamily = languages.filter((language) => primarySubtag(language.iso_code) === primarySubtag(wanted));
          if (!sameFamily.length) continue;

          // The most preferred language the store offers decides: exact tag first ("pt-BR"),
          // then the current language if it belongs to the same family, then the first of the family.
          const match =
            sameFamily.find((language) => language.iso_code.toLowerCase() === wanted) ||
            sameFamily.find((language) => language.iso_code.toLowerCase() === currentTag) ||
            sameFamily[0];

          return match.iso_code.toLowerCase() === currentTag ? null : match;
        }

        return null;
      }

      wasDismissed() {
        try {
          return window.localStorage.getItem(VIRAL_LANGUAGE_BANNER_KEY) === '1';
        } catch (error) {
          return false;
        }
      }

      fill(language, copy) {
        const region = this.querySelector('[data-region]');
        const name = language.endonym_name || language.iso_code;
        const fallback = (text) => (text || '').replace('[language]', name);

        if (copy && region) region.setAttribute('lang', language.iso_code);
        if (region) region.setAttribute('aria-label', (copy && copy.label) || this.dataset.label);

        this.querySelector('[data-message-target]').textContent = copy ? copy.message : fallback(this.dataset.message);
        this.querySelector('[data-action-target]').textContent = copy ? copy.action : fallback(this.dataset.action);
        this.closeButton.setAttribute('aria-label', (copy && copy.dismiss) || this.dataset.dismiss);

        this.form.querySelector('input[name="locale_code"]').value = language.iso_code;
      }

      onSubmit(event) {
        if (this.designMode) {
          event.preventDefault();
          return;
        }

        // Keep ?variant=... and campaign parameters in case the return_to written by the form tag only carries the path.
        const returnTo = this.form.querySelector('input[name="return_to"]');
        if (returnTo && window.location.search && returnTo.value.indexOf('?') === -1) {
          returnTo.value += window.location.search;
        }

        rememberChoice();
      }

      onDismiss() {
        if (!this.designMode) rememberChoice();
        const hadFocus = this.contains(document.activeElement);
        this.hidden = true;
        if (hadFocus) this.focusNext();
      }

      /* Hiding the banner removes the focused close button; without this, focus falls back to <body> and a
         keyboard or screen-reader user starts again from the top. Move on to the next control in the page
         (announcement bar link, menu, logo...). A pointer click does not get a visible ring from this. */
      focusNext() {
        const selector =
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), input:not([type=hidden]):enabled, select:enabled, textarea:enabled";
        for (const element of document.querySelectorAll(selector)) {
          if (!(this.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
          if (this.contains(element) || element.getClientRects().length === 0) continue;
          element.focus({ preventScroll: true });
          if (document.activeElement === element) return;
        }
      }
    }
  );
}
